import React from 'react';
import {
  Box,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSectionStyles } from '@/styles/utilities/hooks';
import { DraggableDialog } from '../../../shared/components/DraggableDialog';

/**
 * Dialog component for displaying FMO inputspec details
 * Shows configuration details and YAML content
 */
const FMOInputspecDetailsDialog = ({
  open,
  onClose,
  inputspecDetails,
  inputspecYaml
}) => {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();

  return (
    <DraggableDialog
      open={open}
      onClose={onClose}
      title="Fragmentation & GAMESS Configuration Details"
      variant="details"
      width={theme.spacing(62)} // 496px in comfortable density - slightly wider
      height="auto" // Let content determine height
    >
      {inputspecDetails && (
        <Box>
          <Box sx={{ ...sectionStyles.sectionContainer, mb: theme.spacing(2) }}>
            <Typography variant="caption" sx={sectionStyles.sectionHeader}>
              CONFIGURATION PARAMETERS
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: theme.spacing(0.75),
              pl: theme.spacing(2),
              py: theme.spacing(1),
              backgroundColor: theme.palette.grey[50],
              borderRadius: theme.spacing(0.5),
              border: `1px solid ${theme.palette.divider}`
            }}>
              {/* Name */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: theme.spacing(1) }}>
                <Typography sx={{ 
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.palette.text.secondary,
                  minWidth: theme.spacing(12),
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}>
                  Name:
                </Typography>
                <Typography sx={{ 
                  fontSize: theme.typography.fontSize.base,
                  color: theme.palette.text.primary,
                  fontWeight: theme.typography.fontWeight.medium
                }}>
                  {inputspecDetails.name}
                </Typography>
              </Box>
              
              {/* Basis Set */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: theme.spacing(1) }}>
                <Typography sx={{ 
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.palette.text.secondary,
                  minWidth: theme.spacing(12),
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}>
                  Basis Set:
                </Typography>
                <Typography sx={{ 
                  fontSize: theme.typography.fontSize.base,
                  color: theme.palette.text.primary,
                  fontWeight: theme.typography.fontWeight.medium
                }}>
                  {inputspecDetails.basis || 'N/A'}
                </Typography>
              </Box>
              
              {/* Cut Tail */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: theme.spacing(1) }}>
                <Typography sx={{ 
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.palette.text.secondary,
                  minWidth: theme.spacing(12),
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}>
                  Cut Tail:
                </Typography>
                <Typography sx={{ 
                  fontSize: theme.typography.fontSize.base,
                  color: theme.palette.text.primary,
                  fontWeight: theme.typography.fontWeight.medium
                }}>
                  {inputspecDetails.cut_tail ? 'Yes' : 'No'}
                </Typography>
              </Box>
              
              {/* Cut Glycine */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: theme.spacing(1) }}>
                <Typography sx={{ 
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.palette.text.secondary,
                  minWidth: theme.spacing(12),
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}>
                  Cut Glycine:
                </Typography>
                <Typography sx={{ 
                  fontSize: theme.typography.fontSize.base,
                  color: theme.palette.text.primary,
                  fontWeight: theme.typography.fontWeight.medium
                }}>
                  {inputspecDetails.cut_glycine ? 'Yes' : 'No'}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {inputspecYaml && (
            <Box sx={sectionStyles.sectionContainer}>
              <Typography variant="caption" sx={sectionStyles.sectionHeader}>
                YAML CONFIGURATION
              </Typography>
              <Box sx={{ 
                maxHeight: theme.spacing(50), // 400px → dynamic
                overflow: 'auto', 
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: theme.spacing(0.5),
                p: theme.spacing(1),
                backgroundColor: theme.palette.grey[900],
                '&::-webkit-scrollbar': {
                  width: theme.spacing(1),
                  height: theme.spacing(1)
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.grey[800]
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.grey[600],
                  borderRadius: theme.spacing(0.5),
                  '&:hover': {
                    backgroundColor: theme.palette.grey[500]
                  }
                }
              }}>
                <Typography
                  component="pre"
                  sx={{ 
                    margin: 0, 
                    fontSize: theme.typography.fontSize.sm, 
                    fontFamily: 'monospace',
                    color: theme.palette.info.light,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                  }}
                >
                  {inputspecYaml}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </DraggableDialog>
  );
};

export default FMOInputspecDetailsDialog;